import React from 'react';
import { Helmet } from 'react-helmet';
import { FiSearch } from 'react-icons/fi';
import '../../styles/FAQ.css';
import FAQSchema from './FAQSchema';
import Breadcrumbs from '../Breadcrumbs';
import SocialMetaTags from '../SocialMetaTags';
import NativeBannerAd from '../NativeBannerAd';
import { useFAQInteractivity } from '../FAQInteractivity';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  
  // Use the same FAQ interactivity hook we use in blog posts
  useFAQInteractivity();

  const faqData = [
    {
      id: 1,
      question: "What is Quick Inbox?",
      answer: "Quick Inbox is a secure temporary email service that provides you with a private email address that self-destructs after 24 hours. It's designed to protect your privacy and help you avoid unwanted spam."
    },
    {
      id: 2,
      question: "Why should I use Quick Inbox?",
      answer: "Quick Inbox is perfect when you need to register on websites you don't fully trust. Many websites require email registration to access content and might later send spam or sell your email address. With Quick Inbox, you get a temporary address that won't exist after 24 hours, eliminating spam risks while still allowing you to verify your registration."
    },
    {
      id: 3,
      question: "How do I use Quick Inbox?",
      answer: "Using Quick Inbox is simple: • Visit our website to get your temporary email address • Copy the provided email address • Use it where needed (registration forms, etc.) • Receive and view messages in real-time • The address automatically expires after 24 hours"
    },
    {
      id: 4,
      question: "Is Quick Inbox secure?",
      answer: "Yes! Quick Inbox is built with privacy and security in mind. Your temporary email address is private and unique to you. Only you can access the inbox, and all emails are permanently deleted after the address expires."
    },
    {
      id: 5,
      question: "Do I need to sign up?",
      answer: "No registration required! Quick Inbox is designed to be fast and simple. Just visit our site, and you'll instantly get a private, secure email address ready to use."
    },
    {
      id: 6,
      question: "What happens after 24 hours?",
      answer: "After 24 hours, your temporary email address expires, and all associated emails are permanently deleted. The address becomes inactive and can't receive new messages."
    },
    {
      id: 7,
      question: "Can I extend the 24-hour period?",
      answer: "Currently, we don't offer extensions beyond the 24-hour period. This helps maintain security and ensures all temporary emails are properly cleared. If you need a longer-term solution, we recommend using a permanent email service."
    },
    {
      id: 13,
      question: "Can I recover emails after the address expires?",
      answer: "No, once the 24-hour period ends, all emails and the address itself are permanently deleted from our servers. This is a security feature to protect your privacy. Make sure to save any important information before the expiration."
    },
    {
      id: 14,
      question: "How does Quick Inbox handle privacy?",
      answer: "We take privacy seriously. We: • Don't store emails beyond the 24-hour period • Don't require any personal information to use the service • Don't track or sell user data • Use encryption for all email communications • Automatically delete all data after expiration"
    },
    {
      id: 15,
      question: "Does Quick Inbox work on mobile devices?",
      answer: "Yes! Quick Inbox is fully responsive and works on all modern mobile devices and tablets. You can create and manage your temporary email address from any device with a web browser."
    }
  ];

  const filteredFAQs = faqData.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const breadcrumbItems = [
    { name: 'Home', path: '/' },
    { name: 'FAQ', path: '/faq' }
  ];

  return (
    <div className="page-container">
      <Helmet>
        <title>Frequently Asked Questions | Quick Inbox Temporary Email Service</title>
        <meta name="description" content="Find answers to common questions about Quick Inbox's temporary email service. Learn how to use disposable email addresses to protect your privacy and avoid spam." />
        <meta name="keywords" content="temporary email FAQ, disposable email questions, how to use temp mail, anonymous email service, email privacy" />
        <link rel="canonical" href="https://quick-inbox.com/faq" />
      </Helmet>
      
      <FAQSchema faqs={faqData} />
      
      <SocialMetaTags
        title="Frequently Asked Questions | Quick Inbox"
        description="Find answers to common questions about Quick Inbox's temporary email service. Learn how to use disposable email addresses to protect your privacy."
        url="https://quick-inbox.com/faq"
        image="https://quick-inbox.com/images/faq-hero.jpg"
      />
      
      <Breadcrumbs items={breadcrumbItems} />
      
      <div className="blog-post">
        <h1>Frequently Asked Questions About Temporary Email</h1>
        <p className="faq-intro">
          Learn everything you need to know about using Quick Inbox temporary email service to protect your privacy online.
        </p>
        
        <NativeBannerAd />
        
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search FAQs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            aria-label="Search frequently asked questions"
          />
        </div>

        <section className="blog-section faq-section">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq) => (
              <div 
                key={faq.id} 
                className="faq-item"
                itemScope 
                itemType="https://schema.org/Question"
              >
                <h3 itemProp="name">{faq.question}</h3>
                <div className="faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">{faq.answer}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="no-results">No FAQs matching your search. Try different keywords.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default FAQ; 